import * as React from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { lighten, rgba } from 'polished';
import { useDispatch } from 'react-redux';

import { dispatch as emit } from '~/emitter';

import { useStateBus } from '~/state/bridge';
import { getAdType, getEndCardEnabled, getLinkAnalyzed } from '~/state/Campaign/selectors';
import { getLeadAdEnabled } from '~/state/LeadAds/selectors';

import { useDomainVerificationQuery } from '~/theseus/hooks/queries';

import ButtonGroup from '~/theseus/components/ButtonGroup';

const openVerificationModal = () => emit({ type: 'open-dns-modal' });

const checkedStyles = ({ checked }) => {
  if (checked) {
    return css`
      color: #666;
      background: ${rgba('#000', 0.05)};
      box-shadow: inset 0 1px ${rgba('#000', 0.0275)};
    `;
  }

  return css`
    background: #fff;
    box-shadow: 0 1px ${rgba('#000', 0.0275)};
  `;
};
const disabledStyles = ({ disabled }) => {
  if (disabled) {
    return css`
      opacity: 60%;
    `;
  }

  return css``;
};

const OptionBase = styled.label<{ disabled?: boolean; checked: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  transition: background 160ms ease;
  border: 1px solid ${lighten(0.76, '#000')};
  cursor: pointer;
  border-radius: 4px;

  ${checkedStyles}
  ${disabledStyles}

  input {
    margin-right: 10px;
  }
`;

type OptionProps = {
  label: string;
} & React.InputHTMLAttributes<HTMLInputElement>;
const Option = (props: OptionProps) => {
  const {
    label, checked, disabled, id, ...rest
  } = props;

  return (
    <OptionBase checked={!!checked} disabled={disabled} htmlFor={id}>
      <input
        type="radio"
        name="ad-type"
        id={id}
        checked={checked}
        disabled={disabled}
        {...rest}
      />
      {label}
    </OptionBase>
  );
};

/**
 * default                 -> Boosted, Ad Only, Post Only
 * no valid funding source ->                   Post Only
 * dns not verified        ->          Ad Only
 * lead ad enabled         ->          Ad Only
 */

function DNSGuardedOption(props) {
  const { label, disabled, ...rest } = props;

  const { data: dnsVerified } = useDomainVerificationQuery();

  if (dnsVerified) {
    return <Option label={label} disabled={disabled} {...rest} />;
  }

  return (
    <OptionBase
      as="button"
      type="button"
      disabled={disabled}
      onClick={openVerificationModal}
      css={css`
        color: red;
      `}
      aria-label={label}
    >
      &#9888;&nbsp;
      {label}
    </OptionBase>
  );
}

function EndCard() {
  const endCardEnabled: boolean = useStateBus(getEndCardEnabled);
  const dispatch = useDispatch();

  const toggleEndCard = React.useCallback(() => {
    dispatch({ type: 'toggleEndCard' });
  }, [dispatch]);

  return (
    <OptionBase checked={endCardEnabled} htmlFor="check-end-card">
      <input
        id="check-end-card"
        type="checkbox"
        name="check-end-card"
        onChange={toggleEndCard}
        checked={endCardEnabled}
      />
      Include End Card with Profile Picture
    </OptionBase>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 20px auto;
`;

interface Props {
  // eslint-disable-next-line
  hideEndCard?: boolean;
}

export const AdTypePicker = (props: Props) => {
  const { hideEndCard = false } = props;

  const adType = useStateBus(getAdType);
  const leadAdEnabled: boolean = useStateBus(getLeadAdEnabled);
  const linkAnalyzed: boolean = useStateBus(getLinkAnalyzed);
  const { data: dnsVerified } = useDomainVerificationQuery();

  const dispatch = useDispatch();


  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'changeAdType', payload: e.target.value });
    },
    [dispatch],
  );

  React.useEffect(() => {
    if (!dnsVerified || leadAdEnabled) {
      dispatch({ type: 'changeAdType', payload: 'ad_only' });
    }
  }, [dispatch, dnsVerified, leadAdEnabled]);

  if (!linkAnalyzed) {
    return null;
  }

  return (
    <Container>
      <ButtonGroup vertical={false}>
        <Option
          id="ad_only"
          value="ad_only"
          checked={adType === 'ad_only'}
          onChange={handleChange}
          label="Ad Only"
        />
        <DNSGuardedOption
          id="boosted"
          value="boosted"
          disabled={leadAdEnabled}
          checked={adType === 'boosted'}
          onChange={handleChange}
          label="Boosted Post"
        />
        <DNSGuardedOption
          id="post_only"
          value="post_only"
          disabled={leadAdEnabled}
          checked={adType === 'post_only'}
          onChange={handleChange}
          label="Post Only"
        />
      </ButtonGroup>
      {!hideEndCard && <EndCard />}
    </Container>
  );
};
